import {
  Box,
  Flex,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
} from "@chakra-ui/react";
import type { KeyboardEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FiFileText } from "react-icons/fi";
import {
  AwardCriterionAnswerChoice,
  type EvalCellDto,
  type EvalSupplierAnswerDto,
} from "../../../../autogen/bff-api";
import { ARROWS, MAX_GRADE, gradeValueText, useGradeKeys } from "../../../../common/award-criteria/grade";
import { hasNoModifier, isExactlyAlt } from "../../../../common/platform";
import { AttachmentRow } from "../../../../common/documents/AttachmentRow";
import { CommentPopover } from "./CommentPopover";
import { presentSupplierAnswer } from "./supplierAnswer";
import { TipTap, isTiptapContentEmpty } from "../../../../common/input/TipTap/TipTap";

interface Scoring {
  points: number | null;
  label: string;
  onChange: (value: number | null) => void;
  onOpen: () => void;
}

interface Props {
  cell: EvalCellDto;
  scoring?: Scoring;
}

const DASH = "–";

const PILL = {
  rounded: "full",
  px: "3",
  py: "1",
  minW: "14",
  fontFamily: "mono",
  fontWeight: "bold",
  fontSize: "sm",
} as const;

const weightedTitleOf = (cell: EvalCellDto, label: string): string | undefined =>
  cell.weightedPoints != null ? `${label}: ${cell.weightedPoints.toFixed(2)}` : undefined;

const ReadOnlyPill = ({ cell, decimals }: { cell: EvalCellDto; decimals: 0 | 2 }) => {
  const { t } = useTranslation();
  const tone = cell.isAutoComputed
    ? { bg: "smChipBlueBg", color: "smChipBlueFg" }
    : { bg: "smBackgroundSecondary", color: "smText" };
  return (
    <Box {...PILL} {...tone} title={weightedTitleOf(cell, t("Weighted score"))}>
      {cell.points != null ? cell.points.toFixed(decimals) : DASH}
    </Box>
  );
};

/** Without `scoring` the cell only compares: a price row is computed, a rejected bid or a closed evaluation is read. */
export const ScoreCell = ({ cell, scoring }: Props) => {
  const { t } = useTranslation();
  const comment = cell.comment ?? "";
  const supplierAnswer = presentSupplierAnswer(cell.supplierAnswer);
  const weightedTitle = weightedTitleOf(cell, t("Weighted score"));

  if (cell.isAutoComputed) {
    return (
      <CellLayout>
        <ReadOnlyPill cell={cell} decimals={2} />
      </CellLayout>
    );
  }

  return (
    <CellLayout
      icons={
        <>
          {supplierAnswer && <SupplierAnswerPopover answer={supplierAnswer} />}
          {comment && <CommentPopover comment={comment} />}
        </>
      }
    >
      {scoring ? <GradePill scoring={scoring} title={weightedTitle} /> : <ReadOnlyPill cell={cell} decimals={0} />}
    </CellLayout>
  );
};

export const GroupScoreCell = ({ cell }: { cell: EvalCellDto }) => (
  <CellLayout>
    <ReadOnlyPill cell={cell} decimals={2} />
  </CellLayout>
);

const CellLayout = ({ children, icons }: { children: ReactNode; icons?: ReactNode }) => (
  <Flex align="center" justify="center">
    {children}
    <Flex w="14" flexShrink={0} ml="1" gap="0.5" align="center">
      {icons}
    </Flex>
  </Flex>
);

const GRADE_PILL = "[role='slider']";

const moveBetweenCells = (from: HTMLElement, key: string) => {
  const cell = from.closest("td");
  const row = cell?.parentElement as HTMLTableRowElement | null;
  const body = row?.parentElement as HTMLTableSectionElement | null;
  if (!cell || !row || !body) return;

  if (key === "ArrowLeft" || key === "ArrowRight") {
    const step = key === "ArrowRight" ? 1 : -1;
    for (let c = cell.cellIndex + step; c >= 0 && c < row.cells.length; c += step) {
      const target = row.cells[c]?.querySelector<HTMLElement>(GRADE_PILL);
      if (target) return target.focus();
    }
    return;
  }

  const rows = Array.from(body.rows);
  const step = key === "ArrowDown" ? 1 : -1;
  for (let r = rows.indexOf(row) + step; r >= 0 && r < rows.length; r += step) {
    const target = rows[r]?.cells[cell.cellIndex]?.querySelector<HTMLElement>(GRADE_PILL);
    if (target) return target.focus();
  }
};

const isCellNavigation = (event: KeyboardEvent<HTMLElement>) =>
  isExactlyAlt(event) && ARROWS.includes(event.key);

const GradePill = ({ scoring, title }: { scoring: Scoring; title?: string }) => {
  const { t } = useTranslation();
  const { points, label, onChange, onOpen } = scoring;
  const { onKeyDown } = useGradeKeys({ value: points, onChange });

  return (
    <Box
      {...PILL}
      role="slider"
      tabIndex={0}
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={MAX_GRADE}
      aria-valuenow={points ?? undefined}
      aria-valuetext={gradeValueText(t, points)}
      title={title}
      cursor="pointer"
      bg={points != null ? "smSuccessSoft" : "smBackgroundSecondary"}
      color={points != null ? "smSuccess" : "smMuted"}
      border="1px solid"
      borderColor="transparent"
      outline="none"
      _hover={{ borderColor: points != null ? "smStatusOkAccent" : "smStatusNeutralAccent" }}
      _focusVisible={{ boxShadow: "smFocusRing" }}
      onClick={(event) => {
        event.currentTarget.focus();
        onOpen();
      }}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (isCellNavigation(event)) {
          event.preventDefault();
          moveBetweenCells(event.currentTarget, event.key);
          return;
        }
        if (hasNoModifier(event) && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          onOpen();
          return;
        }
        onKeyDown(event);
      }}
    >
      {points ?? DASH}
    </Box>
  );
};

// What the supplier wrote on this row, so the grid can be read without leaving it. Read-only, like
// everything else in the cell.
const SupplierAnswerPopover = ({ answer }: { answer: EvalSupplierAnswerDto }) => {
  const { t } = useTranslation();
  const attachments = answer.attachments ?? [];
  const choiceLabel =
    answer.choice === AwardCriterionAnswerChoice.Yes
      ? t("Yes")
      : answer.choice === AwardCriterionAnswerChoice.No
        ? t("No")
        : answer.choice === AwardCriterionAnswerChoice.Partial
          ? t("Partly")
          : null;

  return (
    <Popover placement="bottom" isLazy>
      <PopoverTrigger>
        <IconButton
          aria-label={t("View answer")}
          icon={<FiFileText />}
          size="xs"
          variant="ghost"
          color="smMuted"
        />
      </PopoverTrigger>
      <PopoverContent w="sm">
        <PopoverArrow />
        <PopoverBody>
          {answer.text ? (
            <Text fontSize="sm" whiteSpace="pre-wrap" color="smText">
              {answer.text}
            </Text>
          ) : answer.number != null ? (
            <Text fontSize="sm" fontWeight="medium" color="smText">
              {answer.number}
            </Text>
          ) : choiceLabel ? (
            <Stack spacing="2">
              <Text fontSize="sm" fontWeight="medium" color="smText">
                {choiceLabel}
              </Text>
              {answer.partialElaboration && !isTiptapContentEmpty(answer.partialElaboration) && (
                <TipTap
                  content={answer.partialElaboration}
                  editable={false}
                  hasBorder={false}
                  padding="0"
                  maxHeight="48"
                />
              )}
            </Stack>
          ) : (
            <Text fontSize="sm" color="smMuted">
              {t("No written answer.")}
            </Text>
          )}
          {attachments.length > 0 && (
            <Box mt="3">
              <Text fontSize="xs" color="smMuted" textTransform="uppercase" mb="1">
                {t("Attachments")}
              </Text>
              {attachments.map((attachment) => (
                <AttachmentRow key={attachment.id} attachment={attachment} />
              ))}
            </Box>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
